import { createSlice } from "@reduxjs/toolkit";
import { defaultApiState, PassValidationCompStatus } from "../../common/constants/api-constants";
import { fetchUserProfile } from "./user-profile.thunks";
import { UserProfileState } from "./user-profile.interface";
import { ActionStatus } from "../../common/constants/action-status";
import { updateUserProfile } from "./thunks/update-user-profile.thunk";
import { validateUserPassword } from "./thunks/validate-user-password.thunk";
import { fetchUserProfileQuestions } from "./thunks/fetch-user-questions.thunk";
import { validateUniqueEmail } from "./thunks/validate-unique-user.thunk";
import { fetchSchools } from "./thunks/fetch-schools.thunk";
import { fetchDepartments } from "./thunks/fetch-departments.thunk";
import { fetchDescipline } from "./thunks/fetch-descipline.thunk";
import { userSignout } from "./thunks/user-signout.thunk";
import { updateUserSelf } from "./thunks/fetch-user-self.thunk";
import { fetchSchoolByXid } from './thunks/get-school-by-xid.thunk';

/*----- DECLARE INTERFACES ------*/

export const ROLES: { [key: string]: string } = {
  INS : 'instructor',
  TCH : 'instructor',
  ADM : 'instructor',
  HMS : 'instructor',
  STU : 'learner',
  IND : 'learner',
}

/*----- CREATE INITIAL STATE ------*/

export const userProfileStateInitialState: UserProfileState = {
  userProfileResponse: defaultApiState,
  updateProfile: {
    passCompState: PassValidationCompStatus.default,
    updateUserProfileResponse: defaultApiState,
    password: "",
    uniqueEmailState: defaultApiState,
    schoolResponse: defaultApiState,
    schoolByIdResponse: defaultApiState,
    departmentCache: {},
    desciplineResponse: defaultApiState,
    affiliations: [],
  },
  selfCall: defaultApiState,
  securityQuestionResponse: defaultApiState,
  signoutResponse: defaultApiState
};

/*----- CREATE STORE SLICE ------*/

export const userProfileSlice = createSlice({
  name:"userProfile",
  initialState: userProfileStateInitialState,
  reducers:{
    updatePassValidationCompStatus(state, action) {
      state.updateProfile.passCompState = action.payload;
      if(action.payload === PassValidationCompStatus.default) {
        state.updateProfile.password = "";
      }
    },
    updateAffiliations(state, action) {
      state.updateProfile.affiliations = action.payload || [];
    },
    pushAffiliations(state, action) {
      const arr = state.updateProfile.affiliations || [];
      arr.push(action.payload);
      state.updateProfile.affiliations = arr;
    },
    resetAffiliations(state) {
      state.updateProfile.affiliations = state.userProfileResponse.data?.affiliations;
    },
    resetSchoolResponse(state) {
      state.updateProfile.schoolResponse = defaultApiState;
    },
    initUpdateProfileWithAffiliation(state) {
      state.updateProfile.affiliations = state.userProfileResponse.data?.affiliations;
    }
  },
  extraReducers(builder) {
    builder
    .addCase(fetchUserProfile.pending, (state) => {
      state.userProfileResponse.status = ActionStatus.loading;
    })
    .addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.userProfileResponse.data = action.payload;
      // filter out those affiliations which doesn't have org data
      if(state.userProfileResponse.data?.affiliations) {
        state.userProfileResponse.data.affiliations = state.userProfileResponse.data.affiliations?.filter(data => {
          if(data.orgCity || data.orgCountry || data.orgName || data.orgState) {
            return true;
          }
          return false;
        });
      }
      state.userProfileResponse.status = ActionStatus.succeeded;
    })
    .addCase(fetchUserProfile.rejected, (state, action) => {
      state.userProfileResponse.status = ActionStatus.failed;
      state.userProfileResponse.error = action.payload;
    })

    .addCase(updateUserProfile.pending, (state) => {
      state.updateProfile.updateUserProfileResponse.status = ActionStatus.loading;
    })
    .addCase(updateUserProfile.fulfilled, (state) => {
      state.updateProfile.updateUserProfileResponse.status = ActionStatus.succeeded;
    })
    .addCase(updateUserProfile.rejected, (state, action) => {
      state.updateProfile.updateUserProfileResponse.status = ActionStatus.failed;
      state.updateProfile.updateUserProfileResponse.error = action.payload;
    })

    .addCase(fetchUserProfileQuestions.pending, (state) => {
      state.securityQuestionResponse.status = ActionStatus.loading;
    })
    .addCase(fetchUserProfileQuestions.fulfilled, (state, action) => {
      state.securityQuestionResponse.data = action.payload;
      state.securityQuestionResponse.status = ActionStatus.succeeded;
    })
    .addCase(fetchUserProfileQuestions.rejected, (state, action) => {
      state.securityQuestionResponse.status = ActionStatus.failed;
      state.securityQuestionResponse.error = action.payload;
    })

    .addCase(validateUserPassword.pending, (state) => {
      state.updateProfile.passCompState = PassValidationCompStatus.loading;
    })
    .addCase(validateUserPassword.fulfilled, (state, action) => {
      state.updateProfile.passCompState = PassValidationCompStatus.succeeded;
      state.updateProfile.password = action.payload;
    })
    .addCase(validateUserPassword.rejected, (state) => {
      state.updateProfile.passCompState = PassValidationCompStatus.failed;
      state.updateProfile.password = "";
    })

    .addCase(validateUniqueEmail.pending, (state) => {
      state.updateProfile.uniqueEmailState.status = ActionStatus.loading;
    })
    .addCase(validateUniqueEmail.fulfilled, (state, action) => {
      state.updateProfile.uniqueEmailState.data = action.payload;
      state.updateProfile.uniqueEmailState.status = ActionStatus.succeeded;
    })
    .addCase(validateUniqueEmail.rejected, (state, action) => {
      state.updateProfile.uniqueEmailState.status = ActionStatus.failed;
      state.updateProfile.uniqueEmailState.error = action.payload;
    })

    .addCase(fetchSchools.pending, (state) => {
      state.updateProfile.schoolResponse.status = ActionStatus.loading;
    })
    .addCase(fetchSchools.fulfilled, (state, action) => {
      state.updateProfile.schoolResponse.data = action.payload || [];
      state.updateProfile.schoolResponse.status = ActionStatus.succeeded;
    })
    .addCase(fetchSchools.rejected, (state, action) => {
      state.updateProfile.schoolResponse.status = ActionStatus.failed;
      state.updateProfile.schoolResponse.error = action.payload;
    })

    .addCase(fetchSchoolByXid.pending, (state) => {
      state.updateProfile.schoolByIdResponse.status = ActionStatus.loading;
    })
    .addCase(fetchSchoolByXid.fulfilled, (state, action) => {
      state.updateProfile.schoolByIdResponse.data = action.payload || [];
      state.updateProfile.schoolByIdResponse.status = ActionStatus.succeeded;
    })
    .addCase(fetchSchoolByXid.rejected, (state, action) => {
      state.updateProfile.schoolByIdResponse.status = ActionStatus.failed;
      state.updateProfile.schoolByIdResponse.error = action.payload;
    })

    .addCase(fetchDepartments.pending, (state, action) => {
      const cc = action.meta.arg.countryCode;
      state.updateProfile.departmentCache[cc] = { ...defaultApiState, status: ActionStatus.loading };
    })
    .addCase(fetchDepartments.fulfilled, (state, action) => {
      const { countryCode, data } = action.payload as { countryCode: string; data: any[] };
      state.updateProfile.departmentCache[countryCode] = { ...defaultApiState, data, status: ActionStatus.succeeded };
    })
    .addCase(fetchDepartments.rejected, (state, action) => {
      const cc = action.meta.arg.countryCode;
      state.updateProfile.departmentCache[cc] = { ...defaultApiState, status: ActionStatus.failed, error: action.payload };
    })

    .addCase(fetchDescipline.pending, (state) => {
      state.updateProfile.desciplineResponse.status = ActionStatus.loading;
    })
    .addCase(fetchDescipline.fulfilled, (state, action) => {
      state.updateProfile.desciplineResponse.data = action.payload;
      state.updateProfile.desciplineResponse.status = ActionStatus.succeeded;
    })
    .addCase(fetchDescipline.rejected, (state, action) => {
      state.updateProfile.desciplineResponse.status = ActionStatus.failed;
      state.updateProfile.desciplineResponse.error = action.payload;
    })

    .addCase(userSignout.pending, (state) => {
      state.signoutResponse.status = ActionStatus.loading;
    })
    .addCase(userSignout.fulfilled, (state, action) => {
      state.signoutResponse.data = action.payload;
      state.signoutResponse.status = ActionStatus.succeeded;
      state.userProfileResponse = defaultApiState;
    })
    .addCase(userSignout.rejected, (state, action) => {
      state.signoutResponse.status = ActionStatus.failed;
      state.signoutResponse.error = action.payload;
    })

    .addCase(updateUserSelf.pending, (state) => {
      state.selfCall.status = ActionStatus.loading;
    })
    .addCase(updateUserSelf.fulfilled, (state) => {
      state.selfCall.status = ActionStatus.succeeded;
    })
    .addCase(updateUserSelf.rejected, (state, action) => {
      state.selfCall.status = ActionStatus.failed;
      state.selfCall.error = action.payload;
    })
  }
});

/*----- CREATE SLICE ACTIONS ------*/

export const { updatePassValidationCompStatus, resetSchoolResponse, updateAffiliations, pushAffiliations, resetAffiliations, initUpdateProfileWithAffiliation } = userProfileSlice.actions

/*----- CREATE REDUCER ------*/

export const userProfileReducer = userProfileSlice.reducer;
